import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { parseISO, isValid, isPast } from 'date-fns';
import { ApiError } from '@/api/auth';
import { useQueryClient } from '@tanstack/react-query';
import { useDfsPlayerPool } from '@/hooks/useDfsPlayerPool';
import { statsLeaderboardQueryOptions, contestProjectionsQueryOptions } from '@/hooks/useDfsResearchQueries';
import GoalCelebration from '@/components/common/GoalCelebration';

import {
    fetchAvailablePlayers,
    submitLineup,
    fetchMyLineupForContest,
    updateMyLineup,
    fetchUpcomingContests,
    fetchContestsByDate,
    isNetworkOrTimeoutError,
    fetchContestMatches,
    ApiPlayer,
    ApiMatchInfo,
    LineupSubmissionPayload,
    ApiDfsContest,
    SubmittedLineupResponse,
} from '@/api/dfs';

import { PaginatedAvailablePlayersResponse } from '@/api/schemas';

import CurrentLineup, { LineupPlayer } from '@/components/dfs/CurrentLineup';
import CoachMark from '@/components/common/CoachMark';
import { useCoachMark } from '@/hooks/useCoachMark';
import Spinner from '@/components/common/Spinner';
import CurrentLineupSkeleton from '@/components/dfs/skeletons/CurrentLineupSkeleton';
import StickySummaryBar from '@/components/dfs/StickySummaryBar';
import PlayerPool, { PositionFilter, SortableKeys } from '@/components/dfs/PlayerPool';
import ResearchTab from '@/components/dfs/ResearchTab';
import ProjectionsTab from '@/components/dfs/ProjectionsTab';
import MatchCard from '@/components/dfs/MatchCard';
import MatchCardSkeleton from '@/components/dfs/skeletons/MatchCardSkeleton';
import MatchBreakdownModal from '@/components/dfs/MatchBreakdownModal';
import PlayerProfileModal from '@/components/common/PlayerProfileModal';
import { useAuth } from '@/contexts/AuthContext';
import {
    formatDate,
    formatWeekendLabelDate,
    formatPlayerDisplayName,
} from '@/utils/formatting';
import { mixpanelTrack, mixpanelPeopleIncrement, mixpanelPeopleSet, mixpanelPeopleSetOnce } from '@/services/mixpanel';
import { primaryCompetition } from '@/utils/competition';
import toast from 'react-hot-toast';
import { Drawer } from 'vaul';
import { X } from 'lucide-react';
import { useConfirmDialog } from '@/components/common/ConfirmDialog';
import { buildDfsContestSectionHref } from '@/utils/dfsContestNavigation';
import { userFacingError } from '@/utils/userFacingError';
import { contestRankCompetitionIds } from '@/utils/playerRank';
import { useVenueBrand } from '@/contexts/VenueBrandContext';
import { readVenueCode } from '@/utils/venues';

const logger = console;

const SALARY_CAP_DEFAULT = 100000;
const LINEUP_SIZE = 8;
const POSITION_LIMITS: { [key: string]: number } = { FW: 2, MF: 2, DF: 2, FLEX: 2 };
type SlotType = 'FW' | 'MF' | 'DF' | 'FLEX';

interface DfsLineupBuilderPageProps {
    /** A partner bar's page renders the builder for its own contest, keeping its URL. */
    contestIdOverride?: string;
}

function DfsLineupBuilderPage({ contestIdOverride }: DfsLineupBuilderPageProps = {}) {
    const { token, isAuthenticated, isLoading: isAuthLoading, user } = useAuth();
    const showConfirm = useConfirmDialog();
    const navigate = useNavigate();
    const { contestIdOrAlias: routeContestTarget = 'today' } = useParams<{ contestIdOrAlias: string }>();
    const contestIdOrAlias = contestIdOverride ?? routeContestTarget;
    // Repair legacy share URLs already in circulation without changing the
    // backward-looking `today` semantics used by scorecard endpoints.
    const builderContestTarget = contestIdOrAlias.toLowerCase() === 'today'
        ? 'next'
        : contestIdOrAlias;

    // Essential state management - all preserved from original
    const [pageStatus, setPageStatus] = useState<'loading' | 'no_buildable_contest' | 'contest_over' | 'ready' | 'error'>('loading');
    const [pageError, setPageError] = useState<string | null>(null);
    const [targetContestId, setTargetContestId] = useState<number | null>(null);
    const [contestSalaryCap, setContestSalaryCap] = useState<number>(SALARY_CAP_DEFAULT);
    const [contestDetailsForOprk, setContestDetailsForOprk] = useState<ApiDfsContest | null>(null);
    // A partner bar's contest: its brand, and its entry code goes with the lineup.
    const [venueSlug, setVenueSlug] = useState<string | null>(null);
    useVenueBrand(venueSlug);
    const [lineup, setLineup] = useState<LineupPlayer[]>([]);
    const [lineupPlayerIds, setLineupPlayerIds] = useState<Set<number>>(new Set());
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [existingLineupId, setExistingLineupId] = useState<number | null>(null);
    const [upcomingContests, setUpcomingContests] = useState<ApiDfsContest[]>([]);
    const [isLoadingUpcoming, setIsLoadingUpcoming] = useState<boolean>(false);
    const [lockedPlayerIds, setLockedPlayerIds] = useState<Set<number>>(new Set());
    
    // Modal state
    const [isAddPlayerModalOpen, setIsAddPlayerModalOpen] = useState<boolean>(false);
    const [modalSlotType, setModalSlotType] = useState<SlotType | null>(null);
    const modalScrollContainerRef = useRef<HTMLDivElement>(null);
    const drawerContentRef = useRef<HTMLDivElement>(null);
    const profileModalPortalRef = useRef<HTMLDivElement>(null);
    const firstEmptySlotRef = useRef<HTMLDivElement>(null);
    // The submit/update response, handed to DfsContestPage via router state so
    // it can paint the lineup instantly instead of cold-loading behind a
    // full-screen spinner (the response IS server truth for the lineup).
    const submittedLineupRef = useRef<SubmittedLineupResponse | null>(null);

    const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
    const [selectedPlayerForProfile, setSelectedPlayerForProfile] = useState<ApiPlayer | LineupPlayer | null>(null);

    // One-time onboarding nudge anchored to the first empty lineup slot. Shown
    // only on a brand-new build (page ready, empty lineup) and not while the add
    // tray is open; useCoachMark persists "seen" per device after one tap.
    const { show: showAddSlotCoach, dismiss: dismissAddSlotCoach } = useCoachMark(
        'dfs_builder_first_slot',
        pageStatus === 'ready' && lineup.length === 0 && !isAddPlayerModalOpen
    );

    // Lock the drawer's scroll container while profile modal is open
    useEffect(() => {
        const el = modalScrollContainerRef.current;
        if (!el) return;
        if (isProfileModalOpen) {
            const prevOverflow = el.style.overflow;
            const prevTouchAction = el.style.touchAction;
            const prevOverscrollBehavior = el.style.overscrollBehavior;
            el.style.overflow = 'hidden';
            el.style.touchAction = 'none';
            el.style.overscrollBehavior = 'contain';
            return () => {
                el.style.overflow = prevOverflow;
                el.style.touchAction = prevTouchAction;
                el.style.overscrollBehavior = prevOverscrollBehavior;
            };
        }
    }, [isProfileModalOpen]);

    // Lock the drawer content element while profile modal is open
    useEffect(() => {
        const el = drawerContentRef.current;
        if (!el) return;
        if (isProfileModalOpen) {
            const prevPointerEvents = el.style.pointerEvents;
            const prevTouchAction = el.style.touchAction;
            el.style.pointerEvents = 'none';
            el.style.touchAction = 'none';
            return () => {
                el.style.pointerEvents = prevPointerEvents;
                el.style.touchAction = prevTouchAction;
            };
        }
    }, [isProfileModalOpen]);

    // Register back-button interceptor to close drawer instead of navigating away
    useEffect(() => {
        if (isAddPlayerModalOpen) {
            (window as any).__backButtonInterceptor = () => {
                setIsAddPlayerModalOpen(false);
                return true;
            };
            return () => { delete (window as any).__backButtonInterceptor; };
        } else {
            delete (window as any).__backButtonInterceptor;
        }
    }, [isAddPlayerModalOpen]);

    // Celebration state
    const [showCelebration, setShowCelebration] = useState(false);
    const [celebrationNavTarget, setCelebrationNavTarget] = useState<string | null>(null);

    // Right-panel tab state (declared early so it can gate the full-pool fetch)
    const [activeRightTab, setActiveRightTab] = useState<'pool' | 'research' | 'projections' | 'matches'>('pool');

    // Matches tab state (lazy-loaded the first time the tab is opened)
    const [contestMatches, setContestMatches] = useState<ApiMatchInfo[]>([]);
    const [isLoadingMatches, setIsLoadingMatches] = useState<boolean>(false);
    const [matchesError, setMatchesError] = useState<string | null>(null);
    const [hasFetchedMatches, setHasFetchedMatches] = useState<boolean>(false);
    const [selectedMatchForModal, setSelectedMatchForModal] = useState<ApiMatchInfo | null>(null);
    // Ref guard so the effect can't re-trigger (and cancel) itself when it
    // flips the fetched/loading state. State is only for rendering.
    const matchesFetchedRef = useRef(false);

    // Lazy-load matches the first time the Matches tab is opened.
    useEffect(() => {
        if (activeRightTab !== 'matches' || matchesFetchedRef.current || !targetContestId) return;
        matchesFetchedRef.current = true;
        let cancelled = false;
        setHasFetchedMatches(true);
        setIsLoadingMatches(true);
        setMatchesError(null);
        fetchContestMatches(targetContestId, token)
            .then((matches) => { if (!cancelled) setContestMatches(matches); })
            .catch((err: any) => {
                if (cancelled) return;
                logger.error('[Builder] Failed to load contest matches:', err);
                setMatchesError(userFacingError(err, 'Could not load matches.'));
            })
            .finally(() => { if (!cancelled) setIsLoadingMatches(false); });
        return () => { cancelled = true; };
    }, [activeRightTab, targetContestId, token]);

    // Active matches first (by kickoff), then finished matches - mirrors the contest page.
    const sortedContestMatches = useMemo(() => {
        if (!contestMatches.length) return [];
        const finishedClocks = ['FT', 'AET', 'PEN'];
        const isMatchFinished = (m: ApiMatchInfo) =>
            m.match_clock != null && finishedClocks.includes(m.match_clock.toUpperCase());
        const getTime = (m: ApiMatchInfo) =>
            m.match_date ? new Date(m.match_date).getTime() : 0;
        const active = contestMatches.filter(m => !isMatchFinished(m));
        const finished = contestMatches.filter(m => isMatchFinished(m));
        active.sort((a, b) => getTime(a) - getTime(b));
        finished.sort((a, b) => getTime(a) - getTime(b));
        return [...active, ...finished];
    }, [contestMatches]);

    const handleMatchCardClick = useCallback((match: ApiMatchInfo) => {
        setSelectedMatchForModal(match);
    }, []);

    const handleCloseMatchBreakdownModal = useCallback(() => {
        setSelectedMatchForModal(null);
    }, []);

    // Filter and sort state — shared position filter & search, per-tab sort
    const [activePlayerPoolFilter, setActivePlayerPoolFilter] = useState<PositionFilter>('ALL');
    const [playerPoolSearchTerm, setPlayerPoolSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [playerTabSort, setPlayerTabSort] = useState<{ key: SortableKeys | null; direction: 'asc' | 'desc' }>({ key: 'dfs_salary', direction: 'desc' });
    const [statsTabSort, setStatsTabSort] = useState<{ key: string; direction: 'asc' | 'desc' }>({ key: 'fppg', direction: 'desc' });
    const [projectionsTabSort, setProjectionsTabSort] = useState<{ key: string; direction: 'asc' | 'desc' }>({ key: 'projected', direction: 'desc' });

    // Debounce search input (only affects Players tab API calls)
    useEffect(() => {
        const handler = setTimeout(() => setDebouncedSearchTerm(playerPoolSearchTerm), 400);
        return () => clearTimeout(handler);
    }, [playerPoolSearchTerm]);

    // --- DATA PIPELINE 1: Players tab (paginated, server-side sort/filter) ---
    // Both pool pipelines key on the raw route param (contest id or the
    // 'today' alias - the endpoint resolves aliases server-side) instead of
    // targetContestId. The old gate was component state resolved via an
    // uncached metadata request on EVERY mount, structurally guaranteeing a
    // dash-render for one network round-trip per visit even when the pool
    // data was already cached client-side.
    const playerTabFilters = useMemo(() => ({
        sortBy: playerTabSort.key || 'dfs_salary',
        sortOrder: playerTabSort.direction,
        position: activePlayerPoolFilter,
        search: debouncedSearchTerm,
    }), [playerTabSort, activePlayerPoolFilter, debouncedSearchTerm]);

    const {
        data: paginatedPlayerData,
        error: playerFetchError,
        isLoading: isLoadingPlayers,
        isFetching: isPlayerPoolFetching,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        refetch: refetchPlayerPool,
    } = useDfsPlayerPool(builderContestTarget, playerTabFilters);

    // Flatten players from all loaded pages
    const playerTabPlayers = useMemo(() =>
        paginatedPlayerData?.pages.flatMap(page => page.players) ?? [],
        [paginatedPlayerData]
    );

    // --- DATA PIPELINE 2: Full pool for Stats/Projections (single request, client-side everything) ---
    const FULL_POOL_FILTERS = useMemo(() => ({
        sortBy: 'fppg', sortOrder: 'desc' as const, position: 'ALL', search: '',
    }), []);

    // Prefetch as soon as the Players tab's first page has landed: first paint
    // never competes with this 1,500-row request, but the Stats/Projections
    // tabs are warm ~1s into the visit - long before a user can reach them.
    // (The old gate deferred this to the first tab click AND reset on every
    // mount, causing a dash-flash on each visit; on remount with cached page-1
    // data this now enables immediately and serves the cached full pool.)
    const primaryPoolReady = pageStatus === 'ready' && !!paginatedPlayerData;
    const { data: fullPoolData, isLoading: isFullPoolLoading } = useDfsPlayerPool(
        primaryPoolReady ? builderContestTarget : null,
        FULL_POOL_FILTERS,
        1500
    );


    const fullPoolPlayers = useMemo(() =>
        fullPoolData?.pages.flatMap(page => page.players) ?? [],
        [fullPoolData]
    );

    // Backwards compat: availablePlayers used by other parts of the page (e.g. mobile drawer)
    const availablePlayers = playerTabPlayers;

    // Player locking logic - preserved from original
    const isPlayerLocked = useCallback((player: ApiPlayer | LineupPlayer | null | undefined): boolean => {
        if (!player || !player.match_time) return false;
        try {
            const startTime = parseISO(player.match_time);
            return isValid(startTime) ? isPast(startTime) : false;
        } catch (e) { 
            return false; 
        }
    }, []);

    // Update locked player IDs immediately and then periodically
    useEffect(() => {
        const computeLockedIds = () => {
            const newLockedIds = new Set<number>();
            availablePlayers.forEach((p) => {
                if (isPlayerLocked(p)) newLockedIds.add(p.id);
            });
            lineup.forEach((p) => {
                if (isPlayerLocked(p)) newLockedIds.add(p.id);
            });
            setLockedPlayerIds((current) =>
                current.size !== newLockedIds.size ||
                ![...newLockedIds].every((id) => current.has(id))
                    ? newLockedIds : current
            );
        };

        computeLockedIds(); // Run immediately — no 5-second wait

        const intervalId = setInterval(computeLockedIds, 5000);
        return () => clearInterval(intervalId);
    }, [availablePlayers, lineup, isPlayerLocked]);

    // MODIFIED: Initialize contest metadata only
    useEffect(() => {
        const initializeMetadata = async () => {
            if (isAuthLoading) return;
            
            const currentTarget = builderContestTarget;
            logger.log(`[Builder Init - Target: ${currentTarget}] Starting metadata initialization...`);
            setPageStatus('loading');
            setPageError(null);
            
            try {
                // Fetch minimal data to get contest metadata
                logger.log(`[Builder Init - Target: ${currentTarget}] Fetching contest metadata...`);
                const metadataResponse = await fetchAvailablePlayers(currentTarget, token, { limit: 1 }) as PaginatedAvailablePlayersResponse;
                
                if (!metadataResponse?.contest) {
                    throw new ApiError(
                        currentTarget === 'next'
                            ? "No buildable contest is available right now."
                            : `Contest '${currentTarget}' not found.`,
                        404
                    );
                }
                
                // Set contest metadata to enable the player pool hook
                setTargetContestId(metadataResponse.contest.id);
                setVenueSlug(metadataResponse.contest.venue_slug ?? null);
                setContestSalaryCap(metadataResponse.contest.salary_cap);

                const lastKickoff = metadataResponse.contest.last_match_start_time
                    ? parseISO(metadataResponse.contest.last_match_start_time)
                    : null;
                const contestHasEnded = metadataResponse.contest.is_finalized || (
                    lastKickoff !== null && isValid(lastKickoff) && isPast(lastKickoff)
                );

                if (contestHasEnded) {
                    setPageStatus('contest_over');
                    setPageError('This contest has finished.');
                    try {
                        setIsLoadingUpcoming(true);
                        setUpcomingContests(await fetchUpcomingContests(token, 90));
                    } catch (upcomingErr: unknown) {
                        logger.error('[Builder Init] Error fetching upcoming contests:', upcomingErr);
                    } finally {
                        setIsLoadingUpcoming(false);
                    }
                    return;
                }
                
                // Fetch additional contest details for OPRK if needed
                if (metadataResponse.contest.contest_date) {
                    try {
                        const contestsOnDate = await fetchContestsByDate(metadataResponse.contest.contest_date, token);
                        const fullContestDetails = contestsOnDate.find(c => c.id === metadataResponse.contest.id);
                        setContestDetailsForOprk(fullContestDetails || null);
                    } catch (e) {
                        logger.error(`[Builder Init] Error fetching full contest details`, e);
                    }
                }
                
                setPageStatus('ready');
                logger.log(`[Builder Init] Metadata loaded. Contest ID: ${metadataResponse.contest.id}`);
                
            } catch (fetchError: any) {
                logger.error(`[Builder Init - Target: ${currentTarget}] Error fetching metadata:`, fetchError);
                
                if (fetchError instanceof ApiError) {
                    if (fetchError.status === 404 && currentTarget === 'next') {
                        setPageStatus('no_buildable_contest');
                        setPageError('No buildable contest is available right now.');
                        
                        // Fetch upcoming contests
                        try {
                            setIsLoadingUpcoming(true);
                            const upcoming = await fetchUpcomingContests(token, 90);
                            setUpcomingContests(upcoming);
                        } catch (upcomingErr: unknown) {
                            logger.error('[Builder Init] Error fetching upcoming contests:', upcomingErr);
                        } finally {
                            setIsLoadingUpcoming(false);
                        }
                    } else if (fetchError.status === 403) {
                        setPageStatus('error');
                        setPageError('Access Denied: You cannot build a lineup for this contest type.');
                    } else {
                        setPageStatus('error');
                        setPageError(userFacingError(fetchError, `Failed to load contest '${currentTarget}'.`));
                    }
                } else {
                    setPageStatus('error');
                    setPageError(userFacingError(fetchError, `Unexpected error loading contest '${currentTarget}'.`));
                }
            }
        };
        
        initializeMetadata();
    }, [builderContestTarget, token, isAuthLoading]);

    // Schedule metadata is the primary contest-over signal. This full-pool
    // check is defense in depth for stale/incomplete schedule rows: a builder
    // must never present a slate where every selectable player is locked.
    useEffect(() => {
        if (pageStatus !== 'ready' || isFullPoolLoading || fullPoolPlayers.length === 0) return;
        if (!fullPoolPlayers.every(player => isPlayerLocked(player))) return;

        setPageStatus('contest_over');
        setPageError('This contest has finished.');
        setIsLoadingUpcoming(true);
        void fetchUpcomingContests(token, 90)
            .then(setUpcomingContests)
            .catch((error: unknown) => logger.error('[Builder] Error fetching upcoming contests:', error))
            .finally(() => setIsLoadingUpcoming(false));
    }, [fullPoolPlayers, isFullPoolLoading, isPlayerLocked, pageStatus, token]);

    // SEPARATE: Load existing lineup after players are available
    useEffect(() => {
        const loadExistingLineup = async () => {
            // Guard Clause: Only run if we have a contest ID and token,
            // and if we haven't already loaded an existing lineup.
            if (pageStatus !== 'ready' || !targetContestId || !token || existingLineupId) {
                return;
            }

            try {
                logger.log(`[Builder] Checking for existing lineup for C:${targetContestId}`);
                const existingLineupResponse = await fetchMyLineupForContest(targetContestId, token);

                if (existingLineupResponse) {
                    logger.info(`[Builder] Existing lineup ${existingLineupResponse.id} found with ${existingLineupResponse.players.length} players`);
                    // Clear any stale pendingDfsLineup for this contest — a saved lineup supersedes it.
                    // Covers the silent-commit case: the original submit timed out (499) but the
                    // backend committed anyway, so pendingDfsLineup is no longer needed.
                    try {
                        const pendingStr = localStorage.getItem('pendingDfsLineup');
                        if (pendingStr) {
                            const pending = JSON.parse(pendingStr);
                            if (pending?.contest_id === targetContestId) {
                                localStorage.removeItem('pendingDfsLineup');
                                sessionStorage.removeItem('postLoginAction');
                            }
                        }
                    } catch { /* ignore corrupt localStorage */ }
                    setExistingLineupId(existingLineupResponse.id);
                    setIsEditing(true);

                    // <<< THIS IS THE FIX >>>
                    // The API response (`existingLineupResponse.players`) already contains the
                    // full, detailed player objects. We no longer need to look for them in the
                    // separate, paginated `availablePlayers` state. This eliminates the race condition.

                    const loadedLineup: LineupPlayer[] = [];
                    const loadedIds = new Set<number>();

                    existingLineupResponse.players.forEach((lineupPlayerDetail) => {
                        const fullPlayerData = lineupPlayerDetail.player;

                        if (fullPlayerData) {
                            // Directly use the complete player data from the API response
                            loadedLineup.push({
                                ...fullPlayerData,
                                assigned_slot: lineupPlayerDetail.assigned_position,
                            });
                            loadedIds.add(fullPlayerData.id);
                        } else {
                            // This warning is a safeguard in case the API response is malformed
                            logger.warn(`[Builder] Player ${lineupPlayerDetail.player_id} from existing lineup was missing its detailed player data from the API response.`);
                        }
                    });

                    logger.info(`[Builder] Successfully loaded ${loadedLineup.length} players into lineup`);
                    setLineup(loadedLineup);
                    setLineupPlayerIds(loadedIds);
                }
            } catch (lineupError: any) {
                // This error handling is for when no lineup exists, which is a normal case.
                if (lineupError instanceof ApiError && (lineupError.status === 404 || lineupError.status === 403)) {
                    logger.info(`[Builder] No existing lineup for C:${targetContestId} (404/403). User will build a new one.`);

                    // Attempt to recover a pending lineup stashed from an unauth submit that later failed
                    // (e.g. post-login auto-submit timed out). Only fire if the pending payload targets
                    // this exact contest and we have a usable token.
                    let recovered = false;
                    try {
                        const pendingStr = localStorage.getItem('pendingDfsLineup');
                        if (pendingStr && token) {
                            const pending: LineupSubmissionPayload = JSON.parse(pendingStr);
                            if (pending?.contest_id === targetContestId && Array.isArray(pending.players) && pending.players.length > 0) {
                                logger.info(`[Builder] Recovering pending lineup from localStorage for C:${targetContestId} (${pending.players.length} players)`);
                                const toastId = toast.loading('Restoring your lineup…');
                                try {
                                    const response = await submitLineup(pending, token);
                                    localStorage.removeItem('pendingDfsLineup');
                                    sessionStorage.removeItem('postLoginAction');
                                    toast.dismiss(toastId);
                                    toast.success('Lineup submitted!');
                                    mixpanelTrack('Lineup Recovered from Pending', {
                                        'User ID': user?.id,
                                        'Contest ID': targetContestId,
                                        'Lineup ID': response.id,
                                    });
                                    navigate(buildDfsContestSectionHref(response.contest_id, 'leaderboard'), { replace: true });
                                    recovered = true;
                                } catch (recoverErr: any) {
                                    toast.dismiss(toastId);
                                    logger.error(`[Builder] Failed to auto-submit pending lineup:`, recoverErr);
                                    // Keep pendingDfsLineup so the user can retry (either by rebuilding
                                    // or by navigating away and back)
                                    toast.error("We couldn't save your previous lineup automatically. Please rebuild and try again.");
                                }
                            }
                        }
                    } catch (parseErr) {
                        logger.error(`[Builder] Corrupt pendingDfsLineup, clearing:`, parseErr);
                        localStorage.removeItem('pendingDfsLineup');
                    }

                    if (!recovered) {
                        setIsEditing(false);
                        setExistingLineupId(null);
                    }
                } else {
                    // This handles actual network or server errors
                    logger.error(`[Builder] Error fetching existing lineup:`, lineupError);
                    toast.error("Could not load your saved lineup. You can still build a new one.");
                }
            }
        };

        loadExistingLineup();
    }, [pageStatus, targetContestId, token, existingLineupId]); // The dependency array is now correct and safe.
    // All handler functions remain the same as original
    const openAddPlayerModal = useCallback((slotType: SlotType) => {
        const isDesktop = window.innerWidth >= 1024;
        const filterToApply = slotType === 'FLEX' ? 'ALL' : slotType;

        setActivePlayerPoolFilter(filterToApply);
        setPlayerPoolSearchTerm('');
        setModalSlotType(slotType); // Always track the target slot (desktop + mobile)

        if (!isDesktop) {
            logger.log(`[Builder Mobile] Opening Bottom Sheet with filter: ${filterToApply}`);
            setIsAddPlayerModalOpen(true);
        } else {
            logger.log(`[Builder Desktop] Target slot set to: ${slotType}, filter: ${filterToApply}`);
        }
    }, []);

    // Clear stale submit error when lineup changes (e.g. user swaps a player after a failure)
    useEffect(() => { setSubmitError(null); }, [lineup]);

    const summary = useMemo(() => {
        const totalSalary = lineup.reduce((sum, p) => sum + (p.dfs_salary ?? 0), 0);
        const playersCount = lineup.length;
        return {
            totalSalary,
            remainingSalary: contestSalaryCap - totalSalary,
            playersCount,
            isValid: playersCount === LINEUP_SIZE && totalSalary <= contestSalaryCap,
        };
    }, [lineup, contestSalaryCap]);

    const handlePlayerSelectFromModal = useCallback((player: ApiPlayer) => {
        if (lockedPlayerIds.has(player.id)) {
            toast.error(`Cannot add ${formatPlayerDisplayName(player)} - their match has already started.`);
            return;
        }

        if (lineupPlayerIds.has(player.id)) {
            toast.error(`${formatPlayerDisplayName(player)} is already in your lineup.`);
            return;
        }

        // Determine target slot: use pre-selected slot if set, otherwise auto-assign
        let targetSlot: SlotType | null = modalSlotType;

        // If a specific non-FLEX slot was pre-selected, the player's position
        // must match it. FLEX accepts every position (FW/MF/DF/GK), so there's
        // nothing to reject there.
        if (targetSlot && targetSlot !== 'FLEX') {
            const playerPos = player.primary_position?.toUpperCase();
            if (playerPos !== targetSlot) {
                toast.error(
                    `${formatPlayerDisplayName(player)} (${player.primary_position ?? 'unknown position'}) cannot fill a ${targetSlot} slot.`
                );
                return;
            }
        }

        // If the pre-selected slot is already full, clear it so auto-assignment kicks in
        if (targetSlot) {
            const currentSlotCount = lineup.filter(p => p.assigned_slot === targetSlot).length;
            if (currentSlotCount >= POSITION_LIMITS[targetSlot]) {
                targetSlot = null;
            }
        }

        if (!targetSlot) {
            // Auto-assign: try the player's exact position slot first, then FLEX
            const slotCounts = lineup.reduce((acc: Record<SlotType, number>, p: LineupPlayer) => {
                acc[p.assigned_slot as SlotType] = (acc[p.assigned_slot as SlotType] || 0) + 1;
                return acc;
            }, {} as Record<SlotType, number>);

            const pos = player.primary_position;
            if (pos && ['FW', 'MF', 'DF'].includes(pos)) {
                const posSlot = pos as SlotType;
                if ((slotCounts[posSlot] || 0) < POSITION_LIMITS[posSlot]) {
                    targetSlot = posSlot;
                }
            }
            if (!targetSlot && (slotCounts['FLEX'] || 0) < POSITION_LIMITS['FLEX']) {
                targetSlot = 'FLEX';
            }
            if (!targetSlot) {
                toast.error(`No open slot for ${formatPlayerDisplayName(player)}. Check your lineup.`);
                return;
            }
        }

        const playerToAdd: LineupPlayer = {
            ...player,
            assigned_slot: targetSlot,
            match_time: player.match_time
        };

        setLineup((prevLineup) => [...prevLineup, playerToAdd]);
        setLineupPlayerIds((prevIds) => new Set(prevIds).add(player.id));
        
        // If the slot is now full after this add, clear modalSlotType so next add uses auto-assignment
        const updatedSlotCount = lineup.filter(p => p.assigned_slot === targetSlot).length + 1;
        if (updatedSlotCount >= POSITION_LIMITS[targetSlot]) {
            setModalSlotType(null);
        }

        setIsAddPlayerModalOpen(false);

        mixpanelTrack('Lineup Player Added', {
            'User ID': user?.id,
            'Contest ID': targetContestId,
            'Player ID': player.id,
            'Player Name': formatPlayerDisplayName(player),
            'Player Team': player.current_team_name,
            'Player Salary': player.dfs_salary,
            'Assigned Slot': targetSlot,
            'Lineup Salary After Add': summary.totalSalary + (player.dfs_salary || 0),
            'Players in Lineup Count After Add': lineup.length + 1,
        });
    }, [modalSlotType, lineup, lineupPlayerIds, lockedPlayerIds, user?.id, targetContestId, summary.totalSalary]);

    const handleRemovePlayer = useCallback((playerId: number) => {
        const playerToRemove = lineup.find(p => p.id === playerId);
        if (lockedPlayerIds.has(playerId)) {
            toast.error(`Cannot remove ${formatPlayerDisplayName(playerToRemove)} - their match has already started.`);
            return;
        }
        
        mixpanelTrack('Lineup Player Removed', {
            'User ID': user?.id,
            'Contest ID': targetContestId,
            'Player ID': playerId,
            'Player Name': playerToRemove ? formatPlayerDisplayName(playerToRemove) : 'Unknown',
            'Player Salary': playerToRemove?.dfs_salary,
            'Assigned Slot': playerToRemove?.assigned_slot,
            'Remaining Salary After Remove': summary.totalSalary - (playerToRemove?.dfs_salary || 0),
        });
        
        setLineup(prev => prev.filter(p => p.id !== playerId));
        setLineupPlayerIds(prev => {
            const n = new Set(prev);
            n.delete(playerId);
            return n;
        });
    }, [lineup, lockedPlayerIds, user?.id, targetContestId, summary.totalSalary]);

    const handlePlayerProfileClick = useCallback((player: ApiPlayer | LineupPlayer) => {
        setSelectedPlayerForProfile(player);
        setIsProfileModalOpen(true);
    }, []);

    const handlePlayerPoolProfileClick = useCallback((playerId: number) => {
        const player = availablePlayers.find(p => p.id === playerId);
        if (player) {
            handlePlayerProfileClick(player);
        }
    }, [availablePlayers, handlePlayerProfileClick]);

    const handleClearLineup = useCallback(async () => {
        if (await showConfirm({ message: 'Are you sure you want to remove all non-locked players from your lineup?', confirmLabel: 'Clear', destructive: true })) {
            const unlockedPlayers = lineup.filter(p => !lockedPlayerIds.has(p.id));
            const lockedPlayers = lineup.filter(p => lockedPlayerIds.has(p.id));

            mixpanelTrack('Lineup Cleared', {
                'User ID': user?.id,
                'Contest ID': targetContestId,
                'Players Removed Count': unlockedPlayers.length,
                'Players Locked Count': lockedPlayers.length,
            });

            setLineup(lockedPlayers);
            setLineupPlayerIds(new Set(lockedPlayers.map(p => p.id)));
        }
    }, [lineup, lockedPlayerIds, user?.id, targetContestId, showConfirm]);

    const validationErrors = useMemo(() => {
        const errors: string[] = [];
        if (summary.playersCount !== LINEUP_SIZE) {
            errors.push(`Lineup must have ${LINEUP_SIZE} players (${summary.playersCount}/${LINEUP_SIZE}).`);
        }
        if (summary.totalSalary > contestSalaryCap) {
            errors.push(`Budget cap exceeded ($${summary.totalSalary.toLocaleString()} / $${contestSalaryCap.toLocaleString()}).`);
        }
        if (summary.playersCount === LINEUP_SIZE) {
            const currentSlotCounts = lineup.reduce((counts, p) => {
                counts[p.assigned_slot] = (counts[p.assigned_slot] || 0) + 1;
                return counts;
            }, {} as { [key: string]: number });
            
            for (const slotKey in POSITION_LIMITS) {
                const required = POSITION_LIMITS[slotKey];
                const current = currentSlotCounts[slotKey] || 0;
                if (current !== required) {
                    errors.push(`Incorrect number of ${slotKey.toUpperCase()} players (${current}/${required}).`);
                }
            }
        }
        return errors;
    }, [lineup, summary, contestSalaryCap]);

    const isLineupStructurallyValid = validationErrors.length === 0 && summary.isValid;
    const isAnyPlayerLockedInLineup = useMemo(() => lineup.some((p) => lockedPlayerIds.has(p.id)), [lineup, lockedPlayerIds]);
    const hasClearablePlayers = useMemo(() => lineup.some(p => !lockedPlayerIds.has(p.id)), [lineup, lockedPlayerIds]);
    const canSubmit = isLineupStructurallyValid && !isSubmitting && pageStatus === 'ready';
    const disableSubmitDueToLockForNew = !isEditing && isAnyPlayerLockedInLineup;

    const handleSubmit = async () => {
        if (!isAuthenticated) {
            if (!isLineupStructurallyValid) {
                toast.error("Please fix the errors in your lineup before proceeding.");
                return;
            }
            
            mixpanelTrack('Submit Lineup Attempt (Anonymous)', {
                'Contest ID': targetContestId,
                'Lineup Salary': summary.totalSalary,
            });
            
            const pendingLineup: LineupSubmissionPayload = {
                contest_id: targetContestId!,
                players: lineup.map(p => ({
                    player_id: p.id,
                    assigned_position: p.assigned_slot,
                })),
                ...(venueSlug ? { venue_code: readVenueCode(venueSlug) } : {}),
            };
            
            localStorage.setItem('pendingDfsLineup', JSON.stringify(pendingLineup));
            sessionStorage.setItem('postLoginAction', JSON.stringify({ action: 'submitDfsLineup' }));
            navigate('/signup?reason=lineup_submission');
            return;
        }
        
        if (!canSubmit || !token || targetContestId === null || disableSubmitDueToLockForNew) {
            logger.warn('Submit/Update prevented', {
                canSubmit, isAuthenticated, token: !!token, targetContestId, 
                isSubmitting, pageStatus, isLineupStructurallyValid, disableSubmitDueToLockForNew,
            });
            
            if (isSubmitting) {
                toast.error('Submission already in progress.');
            } else if (disableSubmitDueToLockForNew) {
                toast.error('Cannot submit a new lineup with locked players.');
            } else if (!isLineupStructurallyValid) {
                toast.error(validationErrors[0] || 'Please fix lineup errors before submitting.');
            } else if (!isAuthenticated || !token) {
                toast.error('Authentication issue. Please try logging in again.');
            } else {
                toast.error('Cannot submit lineup at this time. Please check for errors.');
            }
            return;
        }
        
        setIsSubmitting(true);
        setSubmitError(null);
        const payload: LineupSubmissionPayload = {
            contest_id: targetContestId,
            players: lineup.map((p) => ({
                player_id: p.id,
                assigned_position: p.assigned_slot,
            })),
            ...(venueSlug ? { venue_code: readVenueCode(venueSlug) } : {}),
        };

        const eventTypePrefix = isEditing ? 'Lineup Update' : 'Lineup Submit';
        mixpanelTrack(`${eventTypePrefix} Attempt`, {
            'User ID': user?.id,
            'Contest ID': targetContestId,
            'Venue': venueSlug,
            'Is Editing': isEditing,
            'Lineup Details': payload.players.map(p => ({ id: p.player_id, slot: p.assigned_position })),
            'Lineup Salary': summary.totalSalary,
        });

        try {
            let response: SubmittedLineupResponse | undefined;
            let lastError: unknown;

            for (let attempt = 1; attempt <= 2; attempt++) {
                try {
                    if (isEditing && existingLineupId) {
                        logger.info(`Attempting to UPDATE lineup ID: ${existingLineupId} (attempt ${attempt})`);
                        response = await updateMyLineup(existingLineupId, payload, token);
                        logger.info('Lineup updated successfully:', response.id);
                    } else {
                        logger.info(`Attempting to CREATE new lineup (attempt ${attempt})`);
                        response = await submitLineup(payload, token);
                        logger.info('Lineup submitted successfully:', response.id);
                    }
                    break;
                } catch (err) {
                    lastError = err;
                    if (attempt === 1 && isNetworkOrTimeoutError(err)) {
                        logger.warn('Network/timeout error on first attempt, retrying...');
                        continue;
                    }
                    throw err;
                }
            }

            if (!response) throw lastError; // Safety net

            mixpanelTrack(eventTypePrefix === 'Lineup Update' ? 'Lineup Updated' : 'Lineup Submitted', {
                'User ID': user?.id,
                'Contest ID': targetContestId,
                'Lineup ID': response.id,
                'Success': true,
            });
            if (!isEditing) {
                mixpanelPeopleIncrement('Total Contests Entered');
                mixpanelPeopleSetOnce({ 'First Lineup Date': new Date().toISOString().split('T')[0], 'Has Used DFS': true });
            }
            mixpanelPeopleSet({ 'Last Active Date': new Date().toISOString().split('T')[0] });

            submittedLineupRef.current = response;
            if (isEditing) {
                navigate(buildDfsContestSectionHref(targetContestId, 'leaderboard'), {
                    replace: true,
                    state: { submittedLineup: response, contest: contestDetailsForOprk },
                });
            } else {
                // First entry only (never edits): tells the Scorecard to show
                // the post-entry Invite prompt (BAR-FUNNEL step 10b) on arrival.
                sessionStorage.setItem('showSharePromptAfterSubmit', '1');
                setCelebrationNavTarget(buildDfsContestSectionHref(targetContestId, 'leaderboard'));
                setShowCelebration(true);
            }
        } catch (error: any) {
            logger.error(`Error ${isEditing ? 'updating' : 'submitting'} lineup:`, error);

            if (isNetworkOrTimeoutError(error)) {
                setSubmitError("Couldn't connect — your lineup is safe. Tap to try again.");
            } else {
                const errMessage = userFacingError(error, 'Something went wrong saving your lineup. Please try again.');
                toast.error(errMessage);
            }

            mixpanelTrack(`${eventTypePrefix} Failed`, {
                'User ID': user?.id,
                'Contest ID': targetContestId,
                'Error': isNetworkOrTimeoutError(error) ? 'Network/timeout after retry' : (error?.message || 'Unknown'),
                'Lineup Details (on fail)': payload.players.map(p => ({ id: p.player_id, slot: p.assigned_position })),
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSelectUpcomingContest = (contestId: number) => {
        if (!contestId) return;
        logger.log(`[Builder] User selected upcoming contest ID: ${contestId}`);
        // Route params can change without remounting this page. Clear the old
        // contest's local lineup so it can never flash or be submitted against
        // the newly selected contest while metadata reloads.
        setPageStatus('loading');
        setPageError(null);
        setTargetContestId(null);
        setContestDetailsForOprk(null);
        setLineup([]);
        setLineupPlayerIds(new Set());
        setExistingLineupId(null);
        setIsEditing(false);
        setUpcomingContests([]);
        setLockedPlayerIds(new Set());
        navigate(`/dfs/build/${contestId}`);
    };

    // EPL-primary (MULTI-COMP D2): on a mixed-comp slate the page-level OPRK
    // label + competition context resolve to EPL, never to list order.
    const leagueShortNameForOprk = useMemo(() => {
        return primaryCompetition(contestDetailsForOprk?.competitions)?.short_name ?? "EPL";
    }, [contestDetailsForOprk]);

    const competitionId = useMemo(() =>
        primaryCompetition(contestDetailsForOprk?.competitions)?.id ?? 0,
        [contestDetailsForOprk]
    );

    // Warm the Stats/Projections tabs' data alongside the full pool, so the
    // FIRST tab click renders populated (the tabs' own useQuery calls then hit
    // this cache). Prefetch is a no-op when the cache is already fresh.
    const queryClient = useQueryClient();
    useEffect(() => {
        if (!primaryPoolReady) return;
        if (targetContestId) {
            queryClient.prefetchQuery(contestProjectionsQueryOptions(targetContestId, token));
        }
        if (competitionId) {
            queryClient.prefetchQuery(statsLeaderboardQueryOptions(competitionId, token));
        }
    }, [primaryPoolReady, targetContestId, competitionId, token, queryClient]);

    // (activeRightTab declared earlier, near filter/sort state)

    // Determine overall loading state
    const isLoading = pageStatus === 'loading' || (pageStatus === 'ready' && isLoadingPlayers && availablePlayers.length === 0);

    // All render conditions remain the same
    if (isAuthLoading) {
        return (
            <div className="flex flex-col justify-center items-center h-screen bg-primary text-accent-light-blue p-4">
                <Spinner size="lg" color="text-accent-yellow" />
                <p className="mt-3 text-lg">Authenticating...</p>
            </div>
        );
    }
    
    if (pageStatus === 'error') {
        return (
            <div className="min-h-screen bg-primary text-gray-200 font-body">
                <div className="bg-surface-card border-b border-gray-700 shadow-lg px-4 py-4">
                    <h1 className="text-2xl sm:text-3xl font-heading font-bold text-accent-yellow text-center">BUILD LINEUP</h1>
                </div>
                <div className="max-w-2xl mx-auto mt-8 px-4">
                    <div className="bg-surface-card rounded-lg shadow-xl border border-red-700/50 p-6 text-center">
                        <h2 className="text-xl font-semibold text-secondary mb-4">Error Loading Contest</h2>
                        <p className="text-gray-300 mb-6">{pageError}</p>
                        <button 
                            onClick={() => navigate('/contests')} 
                            className="bg-accent-yellow hover:bg-yellow-500 active:bg-yellow-500 active:scale-[0.98] text-primary font-bold py-2 px-4 rounded-lg transition-colors shadow-sm"
                        >
                            Back to Contests
                        </button>
                    </div>
                </div>
            </div>
        );
    }
    
    if (pageStatus === 'no_buildable_contest' || pageStatus === 'contest_over') {
        const contestIsOver = pageStatus === 'contest_over';
        return (
            <div className="min-h-screen bg-primary text-gray-200 font-body">
                <div className="bg-surface-card border-b border-gray-700 shadow-lg px-4 py-4">
                    <h1 className="text-2xl sm:text-3xl font-heading font-bold text-accent-yellow text-center">BUILD LINEUP</h1>
                </div>
                <div className="max-w-2xl mx-auto mt-8 px-4">
                    <div className="bg-surface-card rounded-lg shadow-xl border border-gray-700/50 p-6">
                        <h2 className="text-xl font-semibold text-accent-yellow mb-4 text-center">
                            {contestIsOver ? 'Contest Finished' : 'No Contest Available'}
                        </h2>
                        <p className="text-gray-300 mb-6 text-center">
                            {pageError || 'There is no buildable contest available right now.'}{' '}
                            {upcomingContests.length > 0 ? 'Choose an upcoming contest below.' : ''}
                        </p>

                        {contestIsOver && targetContestId && (
                            <button
                                onClick={() => navigate(buildDfsContestSectionHref(targetContestId, 'leaderboard'))}
                                className="mb-6 w-full rounded-lg border border-gray-600 bg-primary px-4 py-3 font-bold text-accent-light-blue transition-colors hover:border-accent-yellow hover:text-accent-yellow"
                            >
                                View Contest Results
                            </button>
                        )}
                        
                        {isLoadingUpcoming && (
                            <div className="flex justify-center py-4">
                                <Spinner size="md" color="text-accent-yellow" />
                            </div>
                        )}
                        
                        {!isLoadingUpcoming && upcomingContests.length === 0 && (
                            <p className="text-gray-500 italic text-center">No upcoming contests found.</p>
                        )}
                        
                        {!isLoadingUpcoming && upcomingContests.length > 0 && (
                            <div className="space-y-3">
                                <h3 className="font-heading text-lg text-accent-light-blue mb-3 border-t border-gray-700 pt-4">
                                    Upcoming Contests
                                </h3>
                                {upcomingContests.map((contest) => {
                                    const isWeekend = contest.is_weekend_contest ?? false;
                                    const labelPrefix = isWeekend ? 'WEEKEND' : 'DAILY';
                                    const textColorClass = isWeekend ? 'text-accent-yellow' : 'text-accent-light-blue';
                                    const formattedDate = isWeekend 
                                        ? formatWeekendLabelDate(contest.contest_date) 
                                        : formatDate(contest.contest_date);
                                    const fullLabel = `${labelPrefix} - ${formattedDate}`;
                                    
                                    return (
                                        <button
                                            key={`${contest.id}-${isWeekend}`}
                                            onClick={() => handleSelectUpcomingContest(contest.id)}
                                            className="w-full bg-primary hover:bg-primary-dark active:bg-primary-dark p-4 rounded-lg text-center font-semibold transition-all duration-150 group border border-gray-700 hover:border-gray-600 active:border-gray-600 shadow-sm hover:shadow-md active:scale-[0.98]"
                                            aria-label={`Build lineup for ${fullLabel}`}
                                        >
                                            <span className={`font-heading text-lg uppercase transition-colors ${textColorClass} group-hover:text-white group-active:text-white`}>
                                                {fullLabel}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
    
    if (isLoading) {
        return (
            <div className="min-h-screen bg-primary text-gray-200 font-body">
                <div className="bg-surface-card border-b border-gray-700 shadow-lg px-4 py-4">
                    <div className="animate-pulse">
                        <div className="h-8 bg-gray-700 rounded w-48 mx-auto"></div>
                    </div>
                </div>
                <div className="max-w-md mx-auto mt-8 px-4">
                    <div className="space-y-4">
                        <CurrentLineupSkeleton />
                        <div className="bg-surface-card p-4 rounded-lg shadow-lg animate-pulse border border-gray-700/50">
                            <div className="h-4 bg-gray-700 rounded w-3/4 mx-auto mb-2"></div>
                            <div className="h-2.5 bg-gray-700 rounded-full w-full mb-1"></div>
                            <div className="h-3 bg-gray-700 rounded w-1/2 mx-auto"></div>
                        </div>
                        <div className="h-12 bg-gray-600 rounded-lg w-full animate-pulse"></div>
                    </div>
                </div>
            </div>
        );
    }

    if (pageStatus === 'ready') {
        return (
            <>
            <Drawer.Root
                modal={false}
                open={isAddPlayerModalOpen}
                onOpenChange={setIsAddPlayerModalOpen}
                dismissible={!isProfileModalOpen}
                handleOnly={isProfileModalOpen}
            >
                <div className="min-h-screen bg-primary text-gray-200 font-body">
                    {/* Mobile-only header */}
                    <div className="lg:hidden bg-primary px-4 py-2.5 sticky top-0 z-20">
                        <h1 className="text-2xl sm:text-3xl font-heading font-bold text-accent-yellow text-center">
                            {isEditing ? 'EDIT LINEUP' : 'BUILD LINEUP'}
                        </h1>
                    </div>

                    <div className="lg:grid lg:grid-cols-[5fr_7fr] lg:h-[calc(100vh-theme(spacing.16))] lg:overflow-hidden">

                        <div className="flex flex-col lg:h-full lg:overflow-hidden">
                            {/* Desktop-only left column header */}
                            <div className="hidden lg:flex lg:flex-col flex-shrink-0 px-4 py-3 border-b border-gray-700/50 lg:h-[90px] lg:justify-center">
                                <h1 className="font-heading font-bold text-sm text-accent-yellow uppercase tracking-wider text-center mb-2">
                                    {isEditing ? 'EDIT LINEUP' : 'BUILD LINEUP'}
                                </h1>
                                {(() => {
                                    const isOverCap = summary.totalSalary > contestSalaryCap;
                                    const spentPercent = Math.min((summary.totalSalary / contestSalaryCap) * 100, 100);
                                    const remainingDisplay = `$${Math.abs(summary.remainingSalary).toLocaleString()}`;
                                    const salaryColor = isOverCap ? 'text-secondary' : summary.remainingSalary === 0 ? 'text-green-400' : 'text-accent-yellow';
                                    const barColor = isOverCap ? 'bg-secondary' : spentPercent === 100 ? 'bg-green-500' : 'bg-accent-yellow';
                                    return (
                                        <div>
                                            <div className={`text-center text-lg font-heading font-bold ${salaryColor} mb-1`}>
                                                {isOverCap ? `${remainingDisplay} over cap` : `${remainingDisplay} remaining`}
                                            </div>
                                            <div className="w-full h-1.5 rounded-full bg-gray-700/60 overflow-hidden">
                                                <div
                                                    className={`h-full rounded-full transition-all duration-300 ease-out ${barColor}`}
                                                    style={{ width: `${spentPercent}%` }}
                                                />
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>
                            <div className="flex-grow overflow-y-auto pb-64 lg:pb-0">
                                <div className="px-4 py-4 lg:px-3 lg:py-1.5">
                                    <CurrentLineup
                                        lineup={lineup}
                                        onRemovePlayer={handleRemovePlayer}
                                        onAddPlayerClick={openAddPlayerModal}
                                        onPlayerClick={handlePlayerProfileClick}
                                        remainingSalary={summary.remainingSalary}
                                        lockedPlayerIds={lockedPlayerIds}
                                        leagueShortNameForOprkContext={leagueShortNameForOprk}
                                        activeSlot={modalSlotType}
                                        firstEmptySlotRef={firstEmptySlotRef}
                                    />
                                    {showAddSlotCoach && (
                                        <CoachMark
                                            targetRef={firstEmptySlotRef}
                                            text="Tap a slot to add a player to your lineup."
                                            onDismiss={dismissAddSlotCoach}
                                        />
                                    )}
                                </div>
                            </div>
                            
                            <div className="lg:px-4 lg:py-2">
                                <StickySummaryBar
                                    summary={summary}
                                    cap={contestSalaryCap}
                                    onSubmit={handleSubmit}
                                    isSubmitting={isSubmitting}
                                    isEditing={isEditing}
                                    validationErrors={validationErrors}
                                    isAnyPlayerLockedInLineup={isAnyPlayerLockedInLineup}
                                    onClearLineup={handleClearLineup}
                                    hasClearablePlayers={hasClearablePlayers}
                                    hasMobileBottomNav={/^\d+$/.test(contestIdOrAlias)}
                                    submitError={submitError}
                                />
                            </div>
                        </div>

                        <div className="hidden lg:flex lg:flex-col lg:h-full lg:overflow-hidden lg:border-l lg:border-gray-700/30">
                            {/* Right column header: pill tabs + search */}
                            <div className="flex-shrink-0 px-3 py-1.5 border-b border-gray-700/50 space-y-1 lg:h-[90px] lg:justify-center">
                                <div className="inline-flex bg-primary/50 rounded-md p-0.5 gap-0.5 border border-accent-yellow">
                                    {([['pool', 'Players'], ['research', 'Stats'], ['projections', 'Projections'], ['matches', 'Matches']] as const).map(([key, label]) => (
                                        <button
                                            key={key}
                                            type="button"
                                            onClick={() => setActiveRightTab(key)}
                                            className={`px-3 py-1 rounded text-caption font-heading font-bold uppercase tracking-wider transition-colors ${
                                                activeRightTab === key
                                                    ? 'bg-accent-yellow text-primary shadow-sm'
                                                    : 'text-gray-400 hover:text-gray-200 hover:bg-primary/30'
                                            }`}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                                {activeRightTab !== 'matches' && (
                                    <div className="relative">
                                        <input
                                            type="text"
                                            placeholder="Search Players..."
                                            value={playerPoolSearchTerm}
                                            onChange={(e) => setPlayerPoolSearchTerm(e.target.value)}
                                            className="w-full py-1 px-2 rounded bg-gray-900 text-white border border-gray-700 focus:border-accent-yellow focus:outline-none placeholder-gray-500 text-xs font-medium pr-8"
                                        />
                                        {isPlayerPoolFetching && activeRightTab === 'pool' && (
                                            <div className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none">
                                                <Spinner size="sm" color="text-gray-400" />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {activeRightTab === 'pool' ? (
                                <PlayerPool
                                    rankCompetitionIds={contestRankCompetitionIds(contestDetailsForOprk)}
                                    players={playerTabPlayers}
                                    onAddPlayer={handlePlayerSelectFromModal}
                                    lineupPlayerIds={lineupPlayerIds}
                                    lockedPlayerIds={lockedPlayerIds}
                                    positionFilter={activePlayerPoolFilter}
                                    onPositionFilterChange={setActivePlayerPoolFilter}
                                    searchTerm={playerPoolSearchTerm}
                                    onSearchTermChange={setPlayerPoolSearchTerm}
                                    sortConfig={playerTabSort}
                                    onSortConfigChange={setPlayerTabSort}
                                    hideFilters={true}
                                    hideSearch={true}
                                    playerFetchError={playerFetchError ? userFacingError(playerFetchError, 'Players could not be loaded.') : null}
                                    onRetry={() => { void refetchPlayerPool(); }}
                                    remainingSalary={summary.remainingSalary}
                                    leagueShortNameForOprkContext={leagueShortNameForOprk}
                                    onLoadMore={hasNextPage ? fetchNextPage : undefined}
                                    isLoadingMore={isFetchingNextPage}
                                    hasMore={!!hasNextPage}
                                    isSearching={isPlayerPoolFetching}
                                    dfsContestId={targetContestId}
                                />
                            ) : activeRightTab === 'research' ? (
                                <div className="flex flex-col flex-grow min-h-0 p-3">
                                    <ResearchTab
                                        rankCompetitionIds={contestRankCompetitionIds(contestDetailsForOprk)}
                                        contestId={targetContestId!}
                                        competitionId={competitionId}
                                        token={token}
                                        availablePlayers={fullPoolPlayers}
                                        onAddPlayer={handlePlayerSelectFromModal}
                                        lineupPlayerIds={lineupPlayerIds}
                                        lockedPlayerIds={lockedPlayerIds}
                                        remainingSalary={summary.remainingSalary}
                                        positionFilter={activePlayerPoolFilter}
                                        onPositionFilterChange={setActivePlayerPoolFilter}
                                        searchTerm={playerPoolSearchTerm}
                                        onSearchTermChange={setPlayerPoolSearchTerm}
                                        leagueShortNameForOprkContext={leagueShortNameForOprk}
                                        sortConfig={statsTabSort}
                                        onSortConfigChange={setStatsTabSort}
                                        isPoolLoading={isFullPoolLoading}
                                        hideSearch={true}
                                    />
                                </div>
                            ) : activeRightTab === 'projections' ? (
                                <div className="flex flex-col flex-grow min-h-0 p-3">
                                    <ProjectionsTab
                                        rankCompetitionIds={contestRankCompetitionIds(contestDetailsForOprk)}
                                        contestId={targetContestId!}
                                        competitionId={competitionId}
                                        token={token}
                                        availablePlayers={fullPoolPlayers}
                                        onAddPlayer={handlePlayerSelectFromModal}
                                        lineupPlayerIds={lineupPlayerIds}
                                        lockedPlayerIds={lockedPlayerIds}
                                        remainingSalary={summary.remainingSalary}
                                        positionFilter={activePlayerPoolFilter}
                                        onPositionFilterChange={setActivePlayerPoolFilter}
                                        searchTerm={playerPoolSearchTerm}
                                        onSearchTermChange={setPlayerPoolSearchTerm}
                                        leagueShortNameForOprkContext={leagueShortNameForOprk}
                                        sortConfig={projectionsTabSort}
                                        onSortConfigChange={setProjectionsTabSort}
                                        isPoolLoading={isFullPoolLoading}
                                        hideSearch={true}
                                    />
                                </div>
                            ) : (
                                <div className="flex flex-col flex-grow min-h-0 p-3 overflow-y-auto">
                                    {(isLoadingMatches || (!hasFetchedMatches && !matchesError)) ? (
                                        <div className="flex flex-wrap gap-2 content-start">
                                            {Array.from({ length: 6 }).map((_, i) => (
                                                <MatchCardSkeleton key={`builder-match-skel-${i}`} />
                                            ))}
                                        </div>
                                    ) : matchesError ? (
                                        <p role="alert" className="text-center text-gray-500 italic p-2 w-full">{matchesError}</p>
                                    ) : sortedContestMatches.length > 0 ? (
                                        <div className="flex flex-wrap gap-2 content-start">
                                            {sortedContestMatches.map((match) => (
                                                <MatchCard
                                                    key={match.match_id}
                                                    match={match}
                                                    onClick={() => handleMatchCardClick(match)}
                                                    isSelected={selectedMatchForModal?.match_id === match.match_id}
                                                />
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-center text-gray-500 italic p-2 w-full">No matches for this contest.</p>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    <Drawer.Portal>
                        <Drawer.Overlay className="fixed inset-0 bg-black/70 z-40" />
                        <Drawer.Content
                            ref={drawerContentRef}
                            className="fixed inset-x-0 top-0 z-50 flex h-[100dvh] max-h-[100dvh] flex-col bg-primary pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)]"
                            aria-describedby="drawer-description"
                        >
                            <h2 id="drawer-title" className="sr-only">
                                {modalSlotType ? `Add ${modalSlotType === 'FLEX' ? 'Flex' : modalSlotType} Player` : 'Add Player'}
                            </h2>
                            <p id="drawer-description" className="sr-only">
                                Select a player to add to your lineup
                            </p>
                            
                            <div className="p-4 bg-surface-card rounded-t-[10px] flex-shrink-0 relative">
                                <div className="mx-auto w-12 h-1.5 flex-shrink-0 rounded-full bg-gray-600 mb-4" />
                                <button
                                    type="button"
                                    onClick={() => setIsAddPlayerModalOpen(false)}
                                    className="absolute top-3 right-3 p-2 rounded-full text-accent-light-blue hover:bg-primary/40 active:bg-primary/40 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-yellow"
                                    aria-label="Close player selector"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                                <h2 className="font-heading font-bold text-xl text-accent-light-blue text-center" aria-hidden="true">
                                    {modalSlotType ? `Add ${modalSlotType}` : 'Add Player'}
                                </h2>
                                <p className="text-xs text-center mt-1.5 font-bold text-white">
                                    <span className="text-accent-yellow">${summary.remainingSalary.toLocaleString()}</span> remaining
                                    <span className="mx-2 text-white">/</span>
                                    <span className="text-accent-yellow">${Math.round(summary.remainingSalary / Math.max(1, LINEUP_SIZE - summary.playersCount)).toLocaleString()}</span> per slot
                                </p>
                            </div>
                            <div className="p-2 flex-1 overflow-y-auto overscroll-y-contain" ref={modalScrollContainerRef}>
                                <PlayerPool
                                    rankCompetitionIds={contestRankCompetitionIds(contestDetailsForOprk)}
                                    players={availablePlayers}
                                    onAddPlayer={handlePlayerSelectFromModal}
                                    lineupPlayerIds={lineupPlayerIds}
                                    lockedPlayerIds={lockedPlayerIds}
                                    positionFilter={activePlayerPoolFilter}
                                    onPositionFilterChange={setActivePlayerPoolFilter}
                                    searchTerm={playerPoolSearchTerm}
                                    onSearchTermChange={setPlayerPoolSearchTerm}
                                    sortConfig={playerTabSort}
                                    onSortConfigChange={setPlayerTabSort}
                                    hideFilters={false}
                                    playerFetchError={playerFetchError ? userFacingError(playerFetchError, 'Players could not be loaded.') : null}
                                    onRetry={() => { void refetchPlayerPool(); }}
                                    remainingSalary={summary.remainingSalary}
                                    leagueShortNameForOprkContext={leagueShortNameForOprk}
                                    externalScrollContainerRef={modalScrollContainerRef}
                                    onLoadMore={hasNextPage ? fetchNextPage : undefined}
                                    isLoadingMore={isFetchingNextPage}
                                    hasMore={!!hasNextPage}
                                    isSearching={isPlayerPoolFetching}
                                    dfsContestId={targetContestId}
                                    enableResearchCoachMark={isAddPlayerModalOpen}
                                    onPlayerClick={handlePlayerPoolProfileClick}
                                />
                            </div>
                        </Drawer.Content>

                        {/* Portal target for profile modal — inside Drawer.Portal so touches aren't "outside" Vaul's tree */}
                        <div ref={profileModalPortalRef} className="fixed inset-0 z-[70] pointer-events-none">
                            {/* pointer-events-none when empty; modal content sets pointer-events-auto */}
                        </div>
                    </Drawer.Portal>
                </div>
            </Drawer.Root>

            {selectedPlayerForProfile && (
                <PlayerProfileModal
                    rankCompetitionIds={contestRankCompetitionIds(contestDetailsForOprk)}
                    isOpen={isProfileModalOpen}
                    onClose={() => {
                        setIsProfileModalOpen(false);
                        setSelectedPlayerForProfile(null);
                    }}
                    player={selectedPlayerForProfile}
                    competitionId={competitionId}
                    competitionName={leagueShortNameForOprk}
                    dfsContestId={targetContestId}
                    portalContainer={profileModalPortalRef.current}
                    isStacked={selectedMatchForModal !== null}
                />
            )}

            {selectedMatchForModal && targetContestId && (
                <MatchBreakdownModal
                    isOpen={selectedMatchForModal !== null}
                    onClose={handleCloseMatchBreakdownModal}
                    match={selectedMatchForModal}
                    contestId={targetContestId}
                    onPlayerProfileClick={handlePlayerProfileClick}
                />
            )}

            {showCelebration && (
                <GoalCelebration
                    onComplete={() => {
                        setShowCelebration(false);
                        if (celebrationNavTarget) {
                            navigate(celebrationNavTarget, {
                                replace: true,
                                state: { submittedLineup: submittedLineupRef.current, contest: contestDetailsForOprk },
                            });
                        }
                    }}
                    message="GAME ON"
                />
            )}
            </>
        );
    }

    return (
        <div className="flex flex-col justify-center items-center h-screen bg-primary text-gray-500 p-4">
            <Spinner size="md" color="text-gray-400" />
            <p className="mt-2 text-sm">Initializing Builder...</p>
        </div>
    );
}

export default DfsLineupBuilderPage;
